import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  useTheme,
  alpha,
  AppBar,
  Toolbar,
  Slider,
} from '@mui/material';
import {
  TrendingUp,
  Speed,
  Security,
  Analytics,
  AccountBalance,
  Business,
  CloudSync,
  VerifiedUser,
  AutoAwesome,
  RocketLaunch,
  Dashboard,
  SmartToy,
  Payment,
  Inventory,
  ShoppingCart,
  Build,
  TrendingDown,
  Receipt,
  Campaign,
  Assessment,
  Warehouse,
  LocalShipping,
  AdminPanelSettings,
  Calculate,
  AttachMoney,
  CalendarToday,
  Percent,
  ShowChart,
  AccountBalanceWallet,
  CreditCard,
  PhoneAndroid,
  CheckCircle,
  KeyboardArrowUp,
  Person,
} from '@mui/icons-material';
import LeadCaptureModal from './LeadCaptureModal';
import { ReleasepilotAffiliation } from '../../shared-components/branding/ReleasepilotAffiliation';

const HomeScreen: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Loan Calculator State
  const [loanAmount, setLoanAmount] = React.useState(500000); // Default 5 Lakhs
  const [interestRate, setInterestRate] = React.useState(2); // Default 2% per month
  const [tenureMonths, setTenureMonths] = React.useState(12); // Default 12 months
  
  // Lead Capture Modal State
  const [leadModalOpen, setLeadModalOpen] = React.useState(false);

  // Scroll animation refs and state
  const [visibleSections, setVisibleSections] = React.useState<Set<string>>(new Set());
  const [showScrollTop, setShowScrollTop] = React.useState(false);
  const sectionRefs = React.useRef<{ [key: string]: HTMLElement | null }>({});

  // Intersection Observer for scroll animations
  React.useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.getAttribute('data-section-id');
          if (sectionId) {
            setVisibleSections((prev) => {
              const newSet = new Set(prev);
              newSet.add(sectionId);
              return newSet;
            });
          }
        }
      });
    }, observerOptions);

    // Observe all sections
    const refs = Object.values(sectionRefs.current);
    refs.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      refs.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  // Scroll to top button visibility
  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  // Helper function to set section ref
  const setSectionRef = (id: string) => (el: HTMLElement | null) => {
    if (el) {
      sectionRefs.current[id] = el;
    }
  };
  
  // Calculate monthly interest payment
  // Monthly Interest = Principal × (Interest Rate / 100)
  // Example: 100000 × (2 / 100) = 2000
  const calculateMonthlyInterest = (principal: number, monthlyRate: number): number => {
    return principal * (monthlyRate / 100);
  };
  
  const monthlyInterest = calculateMonthlyInterest(loanAmount, interestRate);
  // Total Amount = Principal + (Months × Monthly Interest)
  const totalAmount = loanAmount + (tenureMonths * monthlyInterest);
  const totalInterest = totalAmount - loanAmount;
  
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const features = [
    {
      icon: <Speed sx={{ fontSize: 40 }} />,
      title: 'Lightning Fast Processing',
      description: 'AI-powered approval in minutes, not weeks. Get funds when you need them most.',
      color: '#2196F3',
    },
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: 'Bank-Grade Security',
      description: 'Enterprise-level encryption and compliance. Your data is always protected.',
      color: '#4CAF50',
    },
    {
      icon: <Analytics sx={{ fontSize: 40 }} />,
      title: 'Smart Analytics',
      description: 'Real-time insights into your business finances. Make data-driven decisions.',
      color: '#9C27B0',
    },
    {
      icon: <CloudSync sx={{ fontSize: 40 }} />,
      title: 'Cloud-First Platform',
      description: 'Access your account anywhere, anytime. Seamless integration with business tools.',
      color: '#FF9800',
    },
    {
      icon: <VerifiedUser sx={{ fontSize: 40 }} />,
      title: 'Transparent Pricing',
      description: 'No hidden fees, no surprises. Clear, upfront rates you can trust.',
      color: '#F44336',
    },
    {
      icon: <SmartToy sx={{ fontSize: 40 }} />,
      title: 'AI-Powered Risk Assessment',
      description: 'Advanced algorithms ensure fair evaluation and faster approvals.',
      color: '#00BCD4',
    },
  ];

  const stats = [
    { value: '24-48', label: 'Hours to Approval', suffix: 'hrs' },
    { value: '1-10', label: 'Loan Range', suffix: ' Lakhs' },
    { value: '100%', label: 'Digital Process', suffix: '' },
    { value: '99.9%', label: 'Uptime', suffix: '' },
  ];

  const useCases = [
    {
      icon: <Payment sx={{ fontSize: 40 }} />,
      title: 'Salary Bridge Loans',
      description: 'Pay your employees on time! Many SMEs wait for customer payments that arrive mid-month, but salaries are due on the 1st. Get instant funds to bridge the gap and maintain employee trust.',
      color: '#FF5722',
      featured: true,
    },
    {
      icon: <ShoppingCart sx={{ fontSize: 40 }} />,
      title: 'Raw Material Procurement',
      description: 'Never miss a production deadline. Secure funds quickly to purchase raw materials when bulk discounts are available or when urgent orders require immediate material procurement.',
      color: '#4CAF50',
      featured: true,
    },
    {
      icon: <TrendingDown sx={{ fontSize: 40 }} />,
      title: 'Working Capital Management',
      description: 'Smooth out cash flow fluctuations. Access funds to maintain day-to-day operations during slow business periods or while waiting for receivables.',
      color: '#2196F3',
      featured: false,
    },
    {
      icon: <Inventory sx={{ fontSize: 40 }} />,
      title: 'Inventory Management',
      description: 'Stock up before peak seasons. Get funding to build inventory before demand surges, ensuring you never lose sales due to stockouts.',
      color: '#9C27B0',
      featured: false,
    },
    {
      icon: <Build sx={{ fontSize: 40 }} />,
      title: 'Emergency Equipment Repair',
      description: 'Keep operations running smoothly. When critical machinery breaks down, get funds immediately to repair or replace equipment without halting production.',
      color: '#FF9800',
      featured: false,
    },
    {
      icon: <LocalShipping sx={{ fontSize: 40 }} />,
      title: 'Vendor Payment Delays',
      description: 'Manage payment cycles effectively. When customers delay payments, get instant funds to pay your vendors on time and maintain supplier relationships.',
      color: '#00BCD4',
      featured: false,
    },
    {
      icon: <Receipt sx={{ fontSize: 40 }} />,
      title: 'Tax & Compliance Payments',
      description: 'Stay compliant without stress. Access funds for GST, TDS, and other tax obligations, ensuring your business maintains good standing with authorities.',
      color: '#F44336',
      featured: false,
    },
    {
      icon: <Campaign sx={{ fontSize: 40 }} />,
      title: 'Marketing & Growth Campaigns',
      description: 'Invest in growth opportunities. Launch marketing campaigns, participate in trade shows, or invest in business expansion when opportunities arise.',
      color: '#E91E63',
      featured: false,
    },
    {
      icon: <Warehouse sx={{ fontSize: 40 }} />,
      title: 'Seasonal Cash Flow Gaps',
      description: 'Navigate seasonal businesses smoothly. Cover expenses during off-seasons while preparing for peak periods, ensuring year-round stability.',
      color: '#795548',
      featured: false,
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #00f2fe 100%)',
        backgroundSize: '400% 400%',
        animation: 'gradientShift 15s ease infinite',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
        '@keyframes gradientShift': {
          '0%': { backgroundPosition: '0% 50%' },
          '50%': { backgroundPosition: '100% 50%' },
          '100%': { backgroundPosition: '0% 50%' },
        },
      }}
    >
      {/* Floating Tech Elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          pointerEvents: 'none',
          '& > *': {
            position: 'absolute',
            opacity: 0.1,
            animation: 'float 20s infinite ease-in-out',
          },
        }}
      >
        {[...Array(6)].map((_, i) => (
          <Box
            key={i}
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.3)}, ${alpha(theme.palette.secondary.main, 0.3)})`,
              left: `${(i * 16) % 100}%`,
              top: `${(i * 20) % 100}%`,
              animationDelay: `${i * 2}s`,
              '@keyframes float': {
                '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
                '50%': { transform: 'translateY(-50px) rotate(180deg)' },
              },
            }}
          />
        ))}
      </Box>

      {/* App Bar for Admin Login */}
      <AppBar
        position="fixed"
        sx={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 20px rgba(0, 0, 0, 0.1)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <RocketLaunch />
            Whatzneu
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="contained"
              startIcon={<Person />}
              onClick={() => navigate('/customer/login')}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backdropFilter: 'blur(10px)',
                border: 'none',
                color: 'white',
                fontWeight: 700,
                textTransform: 'none',
                px: 3,
                py: 1.2,
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Customer Login
            </Button>
            <Button
              variant="text"
              onClick={() => navigate('/login')}
              sx={{
                minWidth: 'auto',
                width: 40,
                height: 40,
                padding: 0,
                color: 'rgba(255, 255, 255, 0.6)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.9)',
                },
                transition: 'all 0.2s ease',
              }}
              aria-label="Admin Login"
            >
              <AdminPanelSettings />
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Hero Section */}
        <Box
          sx={{
            pt: { xs: 12, md: 16 },
            pb: { xs: 6, md: 10 },
            textAlign: 'center',
            position: 'relative',
          }}
        >
          {/* Brand Logo/Name */}
          <Box
            sx={{
              mb: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                p: 2,
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              }}
            >
              <RocketLaunch sx={{ fontSize: 48, color: 'white' }} />
            </Box>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #fff 0%, #e0e0e0 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  mb: 0.5,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                }}
              >
                Whatzneu
              </Typography>
              <Chip
                label="New Age Financing for SME Growth"
                sx={{
                  background: 'rgba(255, 255, 255, 0.25)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  height: 32,
                }}
              />
            </Box>
          </Box>
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 3 }}>
            <ReleasepilotAffiliation variant="onDark" sx={{ justifyContent: 'center' }} />
          </Box>

          {/* Main Headline */}
          <Typography
            variant="h1"
            sx={{
              fontWeight: 900,
              mb: 3,
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
              lineHeight: 1.2,
              background: 'linear-gradient(135deg, #fff 0%, #f0f0f0 50%, #fff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              backgroundSize: '200% auto',
              animation: 'textShine 3s linear infinite',
              '@keyframes textShine': {
                '0%': { backgroundPosition: '0% center' },
                '100%': { backgroundPosition: '200% center' },
              },
            }}
          >
            New Age Financing
            <br />
            Focused on SME Growth
          </Typography>

          {/* Subheadline */}
          <Typography
            variant="h5"
            sx={{
              mb: 4,
              color: alpha(theme.palette.common.white, 0.95),
              fontWeight: 400,
              maxWidth: '800px',
              mx: 'auto',
              fontSize: { xs: '1.1rem', md: '1.5rem' },
              textShadow: '0 2px 10px rgba(0,0,0,0.2)',
            }}
          >
            Transform your business with AI-driven financing solutions.
            <br />
            Start with <strong>₹1 Lakh</strong> and grow up to <strong>₹10 Lakhs</strong> based on your payment history.
          </Typography>

          {/* CTA Buttons */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{
              justifyContent: 'center',
              mb: 6,
            }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<RocketLaunch />}
              onClick={() => setLeadModalOpen(true)}
              sx={{
                py: 1.5,
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 700,
                background: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.5)',
                color: 'white',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.35)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Apply Now
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<Dashboard />}
              sx={{
                py: 1.5,
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 700,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.5)',
                color: 'white',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '2px solid rgba(255, 255, 255, 0.8)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Explore Dashboard
            </Button>
          </Stack>

          {/* Stats */}
          <Grid container spacing={3} sx={{ mt: 4, mb: 8 }}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Card
                  sx={{
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      background: 'rgba(255, 255, 255, 0.25)',
                      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
                    },
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        color: 'white',
                        mb: 1,
                        fontSize: { xs: '1.8rem', md: '2.5rem' },
                      }}
                    >
                      {stat.value}
                      <Typography component="span" variant="h6" sx={{ ml: 0.5 }}>
                        {stat.suffix}
                      </Typography>
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: alpha(theme.palette.common.white, 0.9),
                        fontWeight: 500,
                        fontSize: { xs: '0.85rem', md: '1rem' },
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Features Section */}
        <Box
          data-section-id="features"
          ref={setSectionRef('features')}
          sx={{
            py: 8,
            opacity: visibleSections.has('features') ? 1 : 0,
            transform: visibleSections.has('features') ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 0.8s ease-out',
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              mb: 6,
              opacity: visibleSections.has('features') ? 1 : 0,
              animation: visibleSections.has('features') ? 'fadeInUp 0.8s ease-out 0.2s both' : 'none',
            }}
          >
            <Chip
              icon={<AutoAwesome />}
              label="Technology First"
              sx={{
                mb: 2,
                background: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                fontWeight: 700,
                fontSize: '1rem',
                height: 40,
                px: 1,
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: 'white',
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              Built for the Digital Age
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: alpha(theme.palette.common.white, 0.9),
                maxWidth: '700px',
                mx: 'auto',
                fontWeight: 400,
              }}
            >
              Every feature designed with cutting-edge technology specifically for SMEs,
              giving your business the competitive edge it deserves to grow and thrive.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: `linear-gradient(90deg, ${feature.color}, ${alpha(feature.color, 0.5)})`,
                      transform: 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.3s ease',
                    },
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      background: 'rgba(255, 255, 255, 0.25)',
                      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
                      '&::before': {
                        transform: 'scaleX(1)',
                      },
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{
                        color: feature.color,
                        mb: 2,
                        display: 'inline-flex',
                        p: 1.5,
                        borderRadius: 2,
                        background: 'rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: 'white',
                        mb: 1.5,
                        fontSize: { xs: '1.1rem', md: '1.25rem' },
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: alpha(theme.palette.common.white, 0.85),
                        lineHeight: 1.7,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Use Cases Section */}
        <Box sx={{ py: 8 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              icon={<Business />}
              label="Real Business Solutions"
              sx={{
                mb: 2,
                background: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                fontWeight: 700,
                fontSize: '1rem',
                height: 40,
                px: 1,
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: 'white',
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              Solutions for Real Business Challenges
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: alpha(theme.palette.common.white, 0.9),
                maxWidth: '700px',
                mx: 'auto',
                fontWeight: 400,
              }}
            >
              We understand the unique cash flow challenges SMEs face. Our short-term
              loans start from <strong>₹1 Lakh</strong> and can grow up to <strong>₹10 Lakhs</strong> based on
              your payment history. Build trust and unlock higher limits with consistent, timely repayments.
            </Typography>
          </Box>

          {/* Featured Use Cases */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            {useCases
              .filter((uc) => uc.featured)
              .map((useCase, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255, 255, 255, 0.4)',
                      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
                      transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                      opacity: visibleSections.has('use-cases') ? 1 : 0,
                      transform: visibleSections.has('use-cases')
                        ? 'translateY(0)'
                        : 'translateY(40px)',
                      animation: visibleSections.has('use-cases')
                        ? `fadeInUp 0.8s ease-out ${index * 0.15}s both`
                        : 'none',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '6px',
                        background: `linear-gradient(90deg, ${useCase.color}, ${alpha(useCase.color, 0.5)})`,
                        transform: 'scaleX(0)',
                        transformOrigin: 'left',
                        transition: 'transform 0.3s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-10px) scale(1.02)',
                        background: 'rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 16px 50px rgba(0, 0, 0, 0.3)',
                        border: `2px solid ${useCase.color}`,
                        '&::before': {
                          transform: 'scaleX(1)',
                        },
                      },
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 3,
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            color: useCase.color,
                            display: 'inline-flex',
                            p: 2,
                            borderRadius: 3,
                            background: 'rgba(255, 255, 255, 0.3)',
                            boxShadow: `0 4px 15px ${alpha(useCase.color, 0.3)}`,
                            flexShrink: 0,
                          }}
                        >
                          {useCase.icon}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Chip
                            label="Featured"
                            size="small"
                            sx={{
                              mb: 1.5,
                              background: `linear-gradient(135deg, ${useCase.color}, ${alpha(useCase.color, 0.7)})`,
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              height: 24,
                            }}
                          />
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 800,
                              color: 'white',
                              mb: 1.5,
                              fontSize: { xs: '1.3rem', md: '1.5rem' },
                            }}
                          >
                            {useCase.title}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: alpha(theme.palette.common.white, 0.95),
                              lineHeight: 1.8,
                              fontSize: '1rem',
                            }}
                          >
                            {useCase.description}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>

          {/* Other Use Cases Grid */}
          <Grid container spacing={3}>
            {useCases
              .filter((uc) => !uc.featured)
              .map((useCase, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: `linear-gradient(90deg, ${useCase.color}, ${alpha(useCase.color, 0.5)})`,
                        transform: 'scaleX(0)',
                        transformOrigin: 'left',
                        transition: 'transform 0.3s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        background: 'rgba(255, 255, 255, 0.25)',
                        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
                        border: `1px solid ${alpha(useCase.color, 0.5)}`,
                        '&::before': {
                          transform: 'scaleX(1)',
                        },
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          color: useCase.color,
                          mb: 2,
                          display: 'inline-flex',
                          p: 1.5,
                          borderRadius: 2,
                          background: 'rgba(255, 255, 255, 0.2)',
                        }}
                      >
                        {useCase.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: 'white',
                          mb: 1.5,
                          fontSize: { xs: '1.1rem', md: '1.25rem' },
                        }}
                      >
                        {useCase.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: alpha(theme.palette.common.white, 0.85),
                          lineHeight: 1.7,
                        }}
                      >
                        {useCase.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>
        </Box>

        {/* Brand Story Section */}
        <Box
          sx={{
            py: 8,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: 4,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            px: { xs: 3, md: 6 },
            mb: 8,
          }}
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: 'white',
                mb: 3,
                fontSize: { xs: '1.8rem', md: '2.5rem' },
              }}
            >
              Why Choose Whatzneu?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: alpha(theme.palette.common.white, 0.9),
                mb: 3,
                lineHeight: 1.8,
                fontSize: '1.1rem',
              }}
            >
              Whatzneu is the new age financing company focused exclusively on SME growth.
              We're not just another lending platform - we're your growth partner. Our technology-first
              approach ensures small businesses get enterprise-level financial tools designed specifically
              for their unique needs. Our AI-driven platform processes applications in record time while
              maintaining the highest standards of security and transparency.
            </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: alpha(theme.palette.common.white, 0.9),
                  mb: 3,
                  lineHeight: 1.8,
                  fontSize: '1.1rem',
                }}
              >
                Starting from <strong>₹1 Lakh</strong> with the potential to grow up to <strong>₹10 Lakhs</strong>
                based on your payment history, we understand the unique needs of growing businesses.
                Build your credit with us and unlock higher limits through consistent, timely payments.
                Our cloud-native infrastructure ensures 99.9% uptime, so you're never left waiting when you need funds most.
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" gap={2}>
                <Chip
                  icon={<Business />}
                  label="SME Focused"
                  sx={{
                    background: 'rgba(255, 255, 255, 0.25)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  icon={<AccountBalance />}
                  label="1-10 Lakh Range"
                  sx={{
                    background: 'rgba(255, 255, 255, 0.25)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  icon={<TrendingUp />}
                  label="Payment History Based"
                  sx={{
                    background: 'rgba(255, 255, 255, 0.25)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  icon={<TrendingUp />}
                  label="Growth Partners"
                  sx={{
                    background: 'rgba(255, 255, 255, 0.25)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                }}
              >
                {[
                  { icon: <CloudSync />, text: '100% Cloud-Based Infrastructure' },
                  { icon: <Security />, text: 'Enterprise Security Standards' },
                  { icon: <Speed />, text: 'Real-Time Application Processing' },
                  { icon: <Analytics />, text: 'Advanced Business Analytics' },
                ].map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 2,
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.2)',
                        transform: 'translateX(10px)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '1.05rem',
                      }}
                    >
                      {item.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Loan Calculator Section */}
        <Box
          data-section-id="calculator"
          ref={setSectionRef('calculator')}
          sx={{
            background: '#ffffff',
            borderRadius: 4,
            py: 8,
            px: { xs: 3, md: 4 },
            mb: 8,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            position: 'relative',
            overflow: 'hidden',
            opacity: visibleSections.has('calculator') ? 1 : 0,
            transform: visibleSections.has('calculator') ? 'scale(1)' : 'scale(0.95)',
            transition: 'all 0.8s ease-out',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #00f2fe 100%)',
              backgroundSize: '400% 100%',
              animation: 'gradientShift 3s ease infinite',
            },
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              icon={<Calculate />}
              label="Loan Calculator"
              sx={{
                mb: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontWeight: 700,
                fontSize: '1rem',
                height: 40,
                px: 1,
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: '#1a1a1a',
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              Calculate Your Monthly Payment
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: alpha('#1a1a1a', 0.7),
                maxWidth: '700px',
                mx: 'auto',
                fontWeight: 400,
              }}
            >
              Explore flexible loan options and discover how affordable your monthly payments can be.
              Start with ₹1 Lakh and grow your limit up to ₹10 Lakhs.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {/* Calculator Controls */}
            <Grid item xs={12} md={5}>
              <Card
                sx={{
                  height: '100%',
                  background: '#f8f9fa',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  p: 4,
                  borderRadius: 3,
                }}
              >
                {/* Loan Amount Slider */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AttachMoney sx={{ color: '#4CAF50', fontSize: 28 }} />
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#1a1a1a',
                        fontWeight: 700,
                        fontSize: '1.1rem',
                      }}
                    >
                      Loan Amount
                    </Typography>
                  </Box>
                  <Box sx={{ px: 2 }}>
                    <Slider
                      value={loanAmount}
                      onChange={(e, newValue) => setLoanAmount(newValue as number)}
                      min={100000}
                      max={1000000}
                      step={50000}
                      marks={[
                        { value: 100000, label: '1L' },
                        { value: 500000, label: '5L' },
                        { value: 1000000, label: '10L' },
                      ]}
                      sx={{
                        color: '#4CAF50',
                        height: 8,
                        '& .MuiSlider-thumb': {
                          width: 24,
                          height: 24,
                          background: 'white',
                          border: '2px solid #4CAF50',
                          boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                          '&:hover': {
                            boxShadow: '0 6px 16px rgba(76, 175, 80, 0.5)',
                          },
                        },
                        '& .MuiSlider-track': {
                          height: 8,
                          borderRadius: 4,
                        },
                        '& .MuiSlider-rail': {
                          height: 8,
                          borderRadius: 4,
                          background: 'rgba(0, 0, 0, 0.1)',
                        },
                        '& .MuiSlider-mark': {
                          background: 'rgba(0, 0, 0, 0.2)',
                        },
                        '& .MuiSlider-markLabel': {
                          color: '#666',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                        },
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#4CAF50',
                      fontWeight: 800,
                      textAlign: 'center',
                      mt: 2,
                      fontSize: { xs: '1.8rem', md: '2.2rem' },
                    }}
                  >
                    {formatCurrency(loanAmount)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: alpha('#1a1a1a', 0.6),
                      display: 'block',
                      textAlign: 'center',
                      mt: 0.5,
                    }}
                  >
                    Choose any amount between ₹1 Lakh to ₹10 Lakhs
                  </Typography>
                </Box>

                {/* Interest Rate Slider */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Percent sx={{ color: '#2196F3', fontSize: 28 }} />
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#1a1a1a',
                        fontWeight: 700,
                        fontSize: '1.1rem',
                      }}
                    >
                      Monthly Interest Rate
                    </Typography>
                  </Box>
                  <Box sx={{ px: 2 }}>
                    <Slider
                      value={interestRate}
                      onChange={(e, newValue) => setInterestRate(newValue as number)}
                      min={1}
                      max={5}
                      step={0.25}
                      marks={[
                        { value: 1, label: '1%' },
                        { value: 2, label: '2%' },
                        { value: 3, label: '3%' },
                        { value: 4, label: '4%' },
                        { value: 5, label: '5%' },
                      ]}
                      sx={{
                        color: '#2196F3',
                        height: 8,
                        '& .MuiSlider-thumb': {
                          width: 24,
                          height: 24,
                          background: 'white',
                          border: '2px solid #2196F3',
                          boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                          '&:hover': {
                            boxShadow: '0 6px 16px rgba(33, 150, 243, 0.5)',
                          },
                        },
                        '& .MuiSlider-track': {
                          height: 8,
                          borderRadius: 4,
                        },
                        '& .MuiSlider-rail': {
                          height: 8,
                          borderRadius: 4,
                          background: 'rgba(0, 0, 0, 0.1)',
                        },
                        '& .MuiSlider-mark': {
                          background: 'rgba(0, 0, 0, 0.2)',
                        },
                        '& .MuiSlider-markLabel': {
                          color: '#666',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                        },
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#2196F3',
                      fontWeight: 800,
                      textAlign: 'center',
                      mt: 2,
                      fontSize: { xs: '1.8rem', md: '2.2rem' },
                    }}
                  >
                    {interestRate}% per month
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: alpha('#1a1a1a', 0.6),
                      display: 'block',
                      textAlign: 'center',
                      mt: 0.5,
                    }}
                  >
                    Competitive rates starting from 2%
                  </Typography>
                </Box>

                {/* Tenure Slider */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CalendarToday sx={{ color: '#9C27B0', fontSize: 28 }} />
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#1a1a1a',
                        fontWeight: 700,
                        fontSize: '1.1rem',
                      }}
                    >
                      Loan Tenure
                    </Typography>
                  </Box>
                  <Box sx={{ px: 2 }}>
                    <Slider
                      value={tenureMonths}
                      onChange={(e, newValue) => setTenureMonths(newValue as number)}
                      min={3}
                      max={36}
                      step={3}
                      marks={[
                        { value: 3, label: '3M' },
                        { value: 6, label: '6M' },
                        { value: 12, label: '12M' },
                        { value: 24, label: '24M' },
                        { value: 36, label: '36M' },
                      ]}
                      sx={{
                        color: '#9C27B0',
                        height: 8,
                        '& .MuiSlider-thumb': {
                          width: 24,
                          height: 24,
                          background: 'white',
                          border: '2px solid #9C27B0',
                          boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)',
                          '&:hover': {
                            boxShadow: '0 6px 16px rgba(156, 39, 176, 0.5)',
                          },
                        },
                        '& .MuiSlider-track': {
                          height: 8,
                          borderRadius: 4,
                        },
                        '& .MuiSlider-rail': {
                          height: 8,
                          borderRadius: 4,
                          background: 'rgba(0, 0, 0, 0.1)',
                        },
                        '& .MuiSlider-mark': {
                          background: 'rgba(0, 0, 0, 0.2)',
                        },
                        '& .MuiSlider-markLabel': {
                          color: '#666',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                        },
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#9C27B0',
                      fontWeight: 800,
                      textAlign: 'center',
                      mt: 2,
                      fontSize: { xs: '1.8rem', md: '2.2rem' },
                    }}
                  >
                    {tenureMonths} Months
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: alpha('#1a1a1a', 0.6),
                      display: 'block',
                      textAlign: 'center',
                      mt: 0.5,
                    }}
                  >
                    Flexible tenure from 3 to 36 months
                  </Typography>
                </Box>
              </Card>
            </Grid>

            {/* Results Display */}
            <Grid item xs={12} md={7}>
              <Card
                sx={{
                  height: '100%',
                  background: '#f8f9fa',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  p: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                }}
              >
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <ShowChart sx={{ fontSize: 32, color: '#4CAF50' }} />
                    <Typography
                      variant="h5"
                      sx={{
                        color: '#1a1a1a',
                        fontWeight: 800,
                        fontSize: { xs: '1.5rem', md: '1.8rem' },
                      }}
                    >
                      Your Loan Breakdown
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: alpha('#1a1a1a', 0.7),
                      ml: 6,
                    }}
                  >
                    Here's what your loan will look like
                  </Typography>
                </Box>

                {/* Monthly Payment - Highlight */}
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #4CAF50 0%, #2196F3 100%)',
                    borderRadius: 3,
                    p: 3,
                    mb: 3,
                    border: 'none',
                    textAlign: 'center',
                    boxShadow: '0 8px 24px rgba(76, 175, 80, 0.3)',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.95)',
                      mb: 1,
                      fontWeight: 600,
                    }}
                  >
                    Your Monthly Interest Payment
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      color: 'white',
                      fontWeight: 900,
                      mb: 1,
                      fontSize: { xs: '2rem', md: '3rem' },
                    }}
                  >
                    {formatCurrency(monthlyInterest)}
                  </Typography>
                  <Chip
                    label="Affordable & Flexible"
                    sx={{
                      background: 'rgba(255, 255, 255, 0.25)',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                    }}
                  />
                </Box>

                {/* Loan Details Grid */}
                <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        background: 'white',
                        borderRadius: 2,
                        p: 2,
                        height: '100%',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: alpha('#1a1a1a', 0.6),
                          display: 'block',
                          mb: 1,
                        }}
                      >
                        Principal Amount
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#1a1a1a',
                          fontWeight: 700,
                          fontSize: { xs: '1rem', md: '1.2rem' },
                        }}
                      >
                        {formatCurrency(loanAmount)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        background: 'white',
                        borderRadius: 2,
                        p: 2,
                        height: '100%',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: alpha('#1a1a1a', 0.6),
                          display: 'block',
                          mb: 1,
                        }}
                      >
                        Total Interest
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#FF9800',
                          fontWeight: 700,
                          fontSize: { xs: '1rem', md: '1.2rem' },
                        }}
                      >
                        {formatCurrency(totalInterest)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        background: 'white',
                        borderRadius: 2,
                        p: 2.5,
                        border: '2px solid #667eea',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: alpha('#1a1a1a', 0.6),
                          display: 'block',
                          mb: 1,
                          fontWeight: 600,
                        }}
                      >
                        Total Amount to Pay
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{
                          color: '#667eea',
                          fontWeight: 800,
                          fontSize: { xs: '1.3rem', md: '1.6rem' },
                        }}
                      >
                        {formatCurrency(totalAmount)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Positive Message */}
                <Box
                  sx={{
                    mt: 3,
                    p: 2.5,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(33, 150, 243, 0.1) 100%)',
                    border: '1px solid rgba(76, 175, 80, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <TrendingUp sx={{ fontSize: 32, color: '#4CAF50' }} />
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#1a1a1a',
                        fontWeight: 700,
                        mb: 0.5,
                      }}
                    >
                      Great Choice! 🎉
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: alpha('#1a1a1a', 0.7),
                        lineHeight: 1.6,
                      }}
                    >
                      Your loan is tailored to help your business grow. With transparent pricing
                      and flexible terms, you're making a smart financial decision for your SME.
                    </Typography>
                  </Box>
                </Box>

                {/* CTA Button */}
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<RocketLaunch />}
                  fullWidth
                  onClick={() => setLeadModalOpen(true)}
                  sx={{
                    mt: 3,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #6a4190 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Apply for This Loan Now
                </Button>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Payment Terms Section */}
        <Box sx={{ py: 8, mb: 8 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              icon={<Payment />}
              label="Flexible Payment Terms"
              sx={{
                mb: 2,
                background: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                fontWeight: 700,
                fontSize: '1rem',
                height: 40,
                px: 1,
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: 'white',
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              Payment Terms That Work for You
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: alpha(theme.palette.common.white, 0.9),
                maxWidth: '700px',
                mx: 'auto',
                fontWeight: 400,
              }}
            >
              We understand cash flow challenges. That's why we offer flexible payment options
              designed specifically for SME businesses like yours.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {/* Feature 1: Monthly Principal Payment */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  opacity: visibleSections.has('payment-terms') ? 1 : 0,
                  transform: visibleSections.has('payment-terms')
                    ? 'translateY(0)'
                    : 'translateY(40px)',
                  animation: visibleSections.has('payment-terms')
                    ? 'fadeInUp 0.8s ease-out 0.2s both'
                    : 'none',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #4CAF50, rgba(76, 175, 80, 0.5))',
                    transform: 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'transform 0.3s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    background: 'rgba(255, 255, 255, 0.25)',
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
                    '&::before': {
                      transform: 'scaleX(1)',
                    },
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        background: 'rgba(76, 175, 80, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AccountBalanceWallet sx={{ fontSize: 40, color: '#4CAF50' }} />
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: 'white',
                        fontSize: { xs: '1.3rem', md: '1.5rem' },
                      }}
                    >
                      Monthly Principal Payment
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: alpha(theme.palette.common.white, 0.95),
                      lineHeight: 1.8,
                      fontSize: '1.05rem',
                      mb: 2,
                    }}
                  >
                    Pay your principal amount on a monthly basis, and interest only on the outstanding balance.
                    This flexible approach helps you manage cash flow better.
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      background: 'rgba(76, 175, 80, 0.1)',
                      border: '1px solid rgba(76, 175, 80, 0.3)',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: alpha(theme.palette.common.white, 0.9),
                        fontWeight: 600,
                        fontStyle: 'italic',
                      }}
                    >
                      💡 <strong>Example:</strong> Loan ₹5 Lakhs, Pay ₹50K principal monthly + interest only on remaining balance
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Feature 2: Multiple Payment Options */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #2196F3, rgba(33, 150, 243, 0.5))',
                    transform: 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'transform 0.3s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    background: 'rgba(255, 255, 255, 0.25)',
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
                    '&::before': {
                      transform: 'scaleX(1)',
                    },
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        background: 'rgba(33, 150, 243, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PhoneAndroid sx={{ fontSize: 40, color: '#2196F3' }} />
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: 'white',
                        fontSize: { xs: '1.3rem', md: '1.5rem' },
                      }}
                    >
                      Convenient Payment Options
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: alpha(theme.palette.common.white, 0.95),
                      lineHeight: 1.8,
                      fontSize: '1.05rem',
                      mb: 3,
                    }}
                  >
                    Choose the payment method that suits you best. We accept multiple payment options
                    for your convenience.
                  </Typography>
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.5,
                        borderRadius: 2,
                        background: 'rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <PhoneAndroid sx={{ color: '#9C27B0', fontSize: 28 }} />
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                        }}
                      >
                        UPI Payment
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.5,
                        borderRadius: 2,
                        background: 'rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <Payment sx={{ color: '#4CAF50', fontSize: 28 }} />
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                        }}
                      >
                        Cash Payment
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.5,
                        borderRadius: 2,
                        background: 'rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <CreditCard sx={{ color: '#FF9800', fontSize: 28 }} />
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                        }}
                      >
                        Online Payment
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Feature 3: Early Closure Option */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  opacity: visibleSections.has('payment-terms') ? 1 : 0,
                  transform: visibleSections.has('payment-terms')
                    ? 'translateY(0)'
                    : 'translateY(40px)',
                  animation: visibleSections.has('payment-terms')
                    ? 'fadeInUp 0.8s ease-out 0.6s both'
                    : 'none',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #FF9800, rgba(255, 152, 0, 0.5))',
                    transform: 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'transform 0.3s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    background: 'rgba(255, 255, 255, 0.25)',
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
                    '&::before': {
                      transform: 'scaleX(1)',
                    },
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        background: 'rgba(255, 152, 0, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CheckCircle sx={{ fontSize: 40, color: '#FF9800' }} />
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: 'white',
                        fontSize: { xs: '1.3rem', md: '1.5rem' },
                      }}
                    >
                      Early Closure Option
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: alpha(theme.palette.common.white, 0.95),
                      lineHeight: 1.8,
                      fontSize: '1.05rem',
                      mb: 2,
                    }}
                  >
                    You can always close your outstanding loan in the middle of the tenure.
                    Pay interest only till the closure date - no prepayment penalties!
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      background: 'rgba(255, 152, 0, 0.1)',
                      border: '1px solid rgba(255, 152, 0, 0.3)',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: alpha(theme.palette.common.white, 0.9),
                        fontWeight: 600,
                        fontStyle: 'italic',
                      }}
                    >
                      ✅ <strong>No Prepayment Charges:</strong> Save on interest by closing early whenever you're ready
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Friendly Message Box */}
          <Box
            sx={{
              mt: 6,
              p: 4,
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              textAlign: 'center',
            }}
          >
            <Business sx={{ fontSize: 48, color: 'white', mb: 2 }} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: 'white',
                mb: 2,
                fontSize: { xs: '1.5rem', md: '2rem' },
              }}
            >
              Payment Terms That Understand Your Business
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: alpha(theme.palette.common.white, 0.95),
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.8,
                fontSize: '1.1rem',
              }}
            >
              We know that running an SME means dealing with unpredictable cash flows.
              That's why we've designed our payment terms to be flexible and business-friendly.
              Pay when it's convenient for you, using your preferred payment method, and close
              early if you're ready - all without any hidden charges or penalties.
            </Typography>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Chip
                icon={<CheckCircle />}
                label="No Hidden Charges"
                sx={{
                  background: 'rgba(76, 175, 80, 0.25)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  height: 36,
                }}
              />
              <Chip
                icon={<CheckCircle />}
                label="Flexible Repayment"
                sx={{
                  background: 'rgba(33, 150, 243, 0.25)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  height: 36,
                }}
              />
              <Chip
                icon={<CheckCircle />}
                label="Early Closure Allowed"
                sx={{
                  background: 'rgba(255, 152, 0, 0.25)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  height: 36,
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Services Section */}
        <Box
          data-section-id="services"
          ref={setSectionRef('services')}
          sx={{
            py: 8,
            mb: 8,
            opacity: visibleSections.has('services') ? 1 : 0,
            transform: visibleSections.has('services') ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 0.8s ease-out',
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              mb: 6,
              opacity: visibleSections.has('services') ? 1 : 0,
              animation: visibleSections.has('services') ? 'fadeInUp 0.8s ease-out 0.2s both' : 'none',
            }}
          >
            <Chip
              icon={<Business />}
              label="Beyond Financing"
              sx={{
                mb: 2,
                background: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                fontWeight: 700,
                fontSize: '1rem',
                height: 40,
                px: 1,
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: 'white',
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              Comprehensive Business Services
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: alpha(theme.palette.common.white, 0.9),
                maxWidth: '700px',
                mx: 'auto',
                fontWeight: 400,
              }}
            >
              We're not just your lender—we're your growth partner. Explore our range of services
              designed to help SMEs optimize operations, leverage technology, and achieve sustainable growth.
            </Typography>
          </Box>

          <Grid container spacing={4} sx={{ mb: 6 }}>
            {/* Business Consultancy Card */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  opacity: visibleSections.has('services') ? 1 : 0,
                  transform: visibleSections.has('services') ? 'translateY(0)' : 'translateY(30px)',
                  animation: visibleSections.has('services') ? 'fadeInUp 0.8s ease-out 0.3s both' : 'none',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #667eea, rgba(102, 126, 234, 0.5))',
                    transform: 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'transform 0.3s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    background: 'rgba(255, 255, 255, 0.25)',
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
                    '&::before': {
                      transform: 'scaleX(1)',
                    },
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        background: 'rgba(102, 126, 234, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Assessment sx={{ fontSize: 48, color: '#667eea' }} />
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: 'white',
                        fontSize: { xs: '1.5rem', md: '1.8rem' },
                      }}
                    >
                      Business Consultancy
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: alpha(theme.palette.common.white, 0.95),
                      lineHeight: 1.8,
                      fontSize: '1.05rem',
                      mb: 3,
                    }}
                  >
                    We help you identify where you lose money and how you can improve floor efficiency.
                    Our experts analyze your operations, cash flow patterns, and business processes to
                    uncover hidden inefficiencies and revenue leaks.
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <VerifiedUser sx={{ fontSize: 20, color: '#667eea' }} />
                      <Typography
                        variant="body2"
                        sx={{ color: alpha(theme.palette.common.white, 0.9) }}
                      >
                        Cash Flow Analysis & Optimization
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <VerifiedUser sx={{ fontSize: 20, color: '#667eea' }} />
                      <Typography
                        variant="body2"
                        sx={{ color: alpha(theme.palette.common.white, 0.9) }}
                      >
                        Revenue Leak Identification
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <VerifiedUser sx={{ fontSize: 20, color: '#667eea' }} />
                      <Typography
                        variant="body2"
                        sx={{ color: alpha(theme.palette.common.white, 0.9) }}
                      >
                        Floor Efficiency Improvements
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* SaaS Solutions Card */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  opacity: visibleSections.has('services') ? 1 : 0,
                  transform: visibleSections.has('services') ? 'translateY(0)' : 'translateY(30px)',
                  animation: visibleSections.has('services') ? 'fadeInUp 0.8s ease-out 0.5s both' : 'none',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #4CAF50, rgba(76, 175, 80, 0.5))',
                    transform: 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'transform 0.3s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    background: 'rgba(255, 255, 255, 0.25)',
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
                    '&::before': {
                      transform: 'scaleX(1)',
                    },
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        background: 'rgba(76, 175, 80, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CloudSync sx={{ fontSize: 48, color: '#4CAF50' }} />
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: 'white',
                        fontSize: { xs: '1.5rem', md: '1.8rem' },
                      }}
                    >
                      SaaS Solutions Consultation
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: alpha(theme.palette.common.white, 0.95),
                      lineHeight: 1.8,
                      fontSize: '1.05rem',
                      mb: 3,
                    }}
                  >
                    Find the perfect SaaS solutions tailored to your domain and improve your operations.
                    We help you choose, implement, and optimize technology tools that drive efficiency and growth.
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <VerifiedUser sx={{ fontSize: 20, color: '#4CAF50' }} />
                      <Typography
                        variant="body2"
                        sx={{ color: alpha(theme.palette.common.white, 0.9) }}
                      >
                        Domain-Specific SaaS Recommendations
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <VerifiedUser sx={{ fontSize: 20, color: '#4CAF50' }} />
                      <Typography
                        variant="body2"
                        sx={{ color: alpha(theme.palette.common.white, 0.9) }}
                      >
                        SaaS Implementation Support
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <VerifiedUser sx={{ fontSize: 20, color: '#4CAF50' }} />
                      <Typography
                        variant="body2"
                        sx={{ color: alpha(theme.palette.common.white, 0.9) }}
                      >
                        Operations Automation
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Additional Services Preview */}
          <Box
            sx={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              border: '1px solid rgba(255, 255, 255, 0.2)',
              p: 4,
              mb: 4,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: 'white',
                fontWeight: 700,
                mb: 3,
                textAlign: 'center',
              }}
            >
              And More Services to Help Your Business Grow
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {[
                { icon: <Analytics />, label: 'Financial Analytics', color: '#FF9800' },
                { icon: <TrendingUp />, label: 'Growth Strategy', color: '#9C27B0' },
                { icon: <Security />, label: 'Compliance & Risk', color: '#00BCD4' },
                { icon: <Speed />, label: 'Process Automation', color: '#E91E63' },
              ].map((service, index) => (
                <Grid item xs={6} sm={3} key={index}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.05)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.15)',
                        transform: 'translateY(-5px)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        color: service.color,
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 1,
                      }}
                    >
                      {service.icon}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                      }}
                    >
                      {service.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* CTA Button */}
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<AutoAwesome />}
              onClick={() => navigate('/services')}
              sx={{
                py: 2,
                px: 6,
                fontSize: '1.2rem',
                fontWeight: 700,
                background: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.5)',
                color: 'white',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.35)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Explore All Services
            </Button>
          </Box>
        </Box>

        {/* Footer CTA */}
        <Box
          sx={{
            py: 6,
            textAlign: 'center',
            mb: 4,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: 'white',
              mb: 2,
              fontSize: { xs: '1.8rem', md: '2.5rem' },
            }}
          >
            Ready to Transform Your Business?
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: alpha(theme.palette.common.white, 0.9),
              mb: 4,
              fontWeight: 400,
            }}
          >
            Join hundreds of growing SMEs powered by Whatzneu
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<RocketLaunch />}
            onClick={() => setLeadModalOpen(true)}
            sx={{
              py: 2,
              px: 6,
              fontSize: '1.2rem',
              fontWeight: 700,
              background: 'rgba(255, 255, 255, 0.25)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.5)',
              color: 'white',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.35)',
                transform: 'translateY(-3px)',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Get Started Today
          </Button>
        </Box>
      </Container>

          {/* Footer — Releasepilot */}
          <Box
            sx={{
              py: 4,
              px: 2,
              textAlign: 'center',
              borderTop: '1px solid rgba(255, 255, 255, 0.15)',
              background: 'rgba(0, 0, 0, 0.15)',
            }}
          >
            <ReleasepilotAffiliation variant="onDark" sx={{ justifyContent: 'center' }} />
          </Box>

          {/* Scroll to Top Button */}
          <Box
            sx={{
              position: 'fixed',
              bottom: 32,
              right: 32,
              zIndex: 1000,
              opacity: showScrollTop ? 1 : 0,
              transform: showScrollTop ? 'translateY(0)' : 'translateY(20px)',
              pointerEvents: showScrollTop ? 'auto' : 'none',
              transition: 'all 0.3s ease',
            }}
          >
            <Button
              onClick={scrollToTop}
              variant="contained"
              sx={{
                minWidth: 56,
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a4190 100%)',
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 32px rgba(102, 126, 234, 0.5)',
                },
                transition: 'all 0.3s ease',
              }}
              aria-label="scroll to top"
            >
              <KeyboardArrowUp sx={{ fontSize: 32, color: 'white' }} />
            </Button>
          </Box>

          {/* Lead Capture Modal */}
      <LeadCaptureModal
        open={leadModalOpen}
        onClose={() => setLeadModalOpen(false)}
        initialLoanAmount={loanAmount}
        initialInterestRate={interestRate}
        initialTenureMonths={tenureMonths}
      />
    </Box>
  );
};

export default HomeScreen;
